import type { UtilizationBand } from '../lib/capacityFormat';

export interface DepartmentAllocation {
  id: string;
  ticketId?: string;
  departmentId: string;
  plannedHours: number;
  notes?: string;
  department?: Department;
  ticket?: { id: string; key: string; summary: string };
  createdAt: string;
  /** Non-null = Avium PROJECTED this slice from a confirmed source-field
   *  mapping — it updates on re-projection only; hand edits refuse (409). */
  source?: string | null;
}

export interface Organization {
  id: string;
  name: string;
  slug: string;
}

export type PreferredView = 'leader' | 'scrum_master' | 'ic';

// Scope filter pinned to /today. Server stores as JSON-stringified blob on
// User.preferredScope; client parses to/from this shape. Both arrays empty
// (or the whole field null) = "all teams in the org."
export interface PreferredScope {
  teamIds?: string[];
  departmentIds?: string[];
}

export interface User {
  id: string;
  email: string;
  name: string;
  role: 'admin' | 'manager' | 'viewer';
  superAdmin?: boolean;
  // Cropped-square image data URL (same storage pattern as the company logo).
  // null/absent → the Avatar component falls back to initials.
  avatarUrl?: string | null;
  welcomeSeen?: boolean;
  tourCompletedAt?: string | null;
  // Persona toggle on /today. Null = first-time user; UI defaults to 'leader'
  // until they pick. See `/today` section in CLAUDE.md.
  preferredView?: PreferredView | null;
  // JSON-stringified PreferredScope from the server (or null = no pin = all).
  // Client parses on read, stringifies on write through authApi.setPreferredScope.
  preferredScope?: string | null;
  // Canonical persona (one of the 6 PersonaKey in lib/personas.ts). Null = not
  // yet captured → the first-run role prompt asks. Drives the /today lens + the
  // daily-briefing framing; supersedes preferredView (derived from its lens).
  persona?: string | null;
  // True when the user must accept the current privacy policy before using the
  // app. New signups accept at account creation (so this is false); existing
  // users who never accepted (or accepted a superseded version) get true and are
  // shown the PrivacyAcceptanceGate. Server-computed — see server/src/lib/legal.ts.
  needsPrivacyAcceptance?: boolean;
  lastLoginAt?: string;
  createdAt?: string;
}

export interface Department {
  id: string;
  name: string;
  color: string;
  order: number;
}

export interface Team {
  id: string;
  name: string;
  color: string;
  // The person who leads this team (team lead / scrum master / process owner).
  // Optional — null until an admin sets it on Departments & Teams. `lead` is the
  // denormalized {id,name} the API returns alongside.
  leadResourceId?: string | null;
  lead?: { id: string; name: string } | null;
}

export interface Resource {
  id: string;
  name: string;
  email: string;
  jiraAccountId?: string;
  departmentId?: string;
  teamId?: string;
  department?: Department;
  team?: Team;
  customFields?: Record<string, unknown>;
  isActive: boolean;
  isDemoData?: boolean;
  capacity?: number;
  pointsCapacity?: number;
  fte?: number;
  timeLogs?: TimeLog[];
  /** The SOURCE tool's own reported time-spent (Jira/ADO/Asana). Undated lifetime total.
   *  Avium-native `timeLogs` win where they exist; this is what every connector org actually
   *  has, and reading only `timeLogs` is why their boards showed no logged hours at all. */
  timeSpentHours?: number | null;
  ticketResources?: TicketResource[];
}

export type StageGroup = 'backlog' | 'in_progress' | 'done';

// Connector import preview (AVM-219 Phase B.0-ii; hoisted here from
// WizardChrome per AVM-221 item 6). "Here's exactly what will land" — a small
// sample of real rows read through the connection's own scope, shared by
// Jira · Azure DevOps · Asana (rule 17). The server twin is
// `ConnectorImportPreview`/`PreviewSampleRow` in
// `server/src/lib/connectorPreview.ts`; a parity test guards the two shapes.
// `stage` is a StageGroup KEY on the wire (AVM-221 item 3) — the label is
// rendered client-side via `lib/stageVocabulary`.
export interface PreviewSampleRow {
  key: string;
  title: string;
  /** The source's own status/state/section name — verbatim. */
  status: string;
  /** Avium's reading (null when the source gave no signal — shown honestly). */
  stage: StageGroup | null;
  assignee: string | null;
}

export interface ImportPreview {
  /** Exact (Azure), approximate (Jira), or null = unknown until first sync (Asana). */
  total: number | null;
  sampleSize: number;
  sample: PreviewSampleRow[];
  /** Distinct assignees seen IN THE SAMPLE — labeled per-sample on the surface. */
  peopleInSample: number;
}

// Status — Avium's normalized workflow status (renamed from Phase 2026-05-24
// for Jira-API alignment; the underlying Prisma model + DB table stay named
// "Phase" via @@map so this is a zero-migration rename). The deprecated
// `Phase = Status` alias was retired 2026-06-17 (AVM-066) — use `Status`.
export interface Status {
  id: string;
  name: string;
  color: string;
  headerColor: string;
  order: number;
  visible: boolean;
  stageGroup: StageGroup;
  // Status Model 1.0 (AVM-159): set when an admin (or the import wizard's
  // explicit classification) deliberately chose the stage — sync auto-
  // derivation never overwrites it. The StatusMapping remap type is retired:
  // native statuses pass through 1:1 as their own columns.
  stageCustomizedAt?: string | null;
  _count?: { tickets: number };
}

export interface JiraProject {
  id: string;
  jiraId: string;
  key: string;
  name: string;
  projectType?: string;
  avatarUrl?: string;
  isDemoData?: boolean;
}

// The unified "project" shape from /api/source-projects (imported OR Avium-native).
// `editable` is true only for source='avium' (imported projects are connector-owned).
export type ProjectSource = 'jira' | 'azure' | 'asana' | 'avium' | 'excel';

// Sources Avium fully owns — no live connector to resync from (mirrors the
// server's lib/projectSources). Owned projects are editable and a valid target
// for native work; connector-synced projects (jira/azure/asana) are read-only.
export const OWNED_PROJECT_SOURCES: ProjectSource[] = ['avium', 'excel'];
export const isOwnedProjectSource = (s: string): boolean =>
  (OWNED_PROJECT_SOURCES as string[]).includes(s);

// One home for the human label of each project source (badge + picker provenance).
export const PROJECT_SOURCE_LABEL: Record<ProjectSource, string> = {
  jira: 'Jira', azure: 'Azure DevOps', asana: 'Asana', avium: 'Avium', excel: 'Excel',
};

export interface Project {
  id: string;
  source: ProjectSource;
  key: string;
  name: string;
  description: string | null;
  projectType: string | null;
  archived: boolean;
  ticketCount: number;
  editable: boolean;
}

export interface Sprint {
  id: string;
  jiraId: number;
  name: string;
  state: 'active' | 'closed' | 'future';
  source?: 'jira' | 'avium';
  startDate?: string;
  endDate?: string;
  projectId?: string;
  project?: JiraProject;
}

export interface FixVersion {
  id: string;
  jiraId: string;
  name: string;
  released: boolean;
  source?: 'jira' | 'avium';
  releaseDate?: string;
  projectId?: string;
}

export interface JiraTicket {
  id: string;
  jiraId: string;
  key: string;
  summary: string;
  description?: string;
  issueType: string;
  // Raw source status NAME (e.g. "In Progress"). Renamed from `status` →
  // `jiraStatusName` 2026-06-17 (AVM-017) to match the server field and
  // disambiguate from the Avium Status relation (`workflowStatus`) + `statusId`.
  jiraStatusName: string;
  priority?: string;
  assigneeId?: string;
  assigneeName?: string;
  assigneeResourceId?: string | null;
  reporterName?: string;
  // First-class team on the ticket (independent of assignee.team). When set,
  // the ticket counts toward this team in capacity rollups; falls back to
  // assignee.team when null. Added 2026-05-24 with the JiraTicket.teamId
  // schema promotion.
  teamId?: string | null;
  team?: Team | null;
  storyPoints?: number;
  plannedHours?: number;
  dueDate?: string;
  projectId?: string;
  project?: JiraProject;
  statusId?: string;
  // The Avium Status this ticket maps to. Named `workflowStatus` to match the
  // server Prisma relation (AVM-017 finish 2026-06-17, was `phase` — the old
  // name silently read `undefined` off the raw /tickets endpoints).
  workflowStatus?: Status;
  sprintId?: string;
  sprint?: Sprint;
  fixVersionId?: string;
  fixVersion?: FixVersion;
  epicKey?: string;
  epicSummary?: string;
  parentKey?: string;
  parentId?: string;
  parent?: JiraTicket;
  children?: JiraTicket[];
  rank?: number;
  dataSource?: 'jira_import' | 'jira_modified' | 'azure_import' | 'azure_modified' | 'asana_import' | 'asana_modified' | 'excel_import' | 'erm_created';
  level?: 'project' | 'feature' | 'task';
  jiraCreatedAt?: string;
  jiraUpdatedAt?: string;
  // When the ticket last entered its current status CATEGORY (To Do / In Progress
  // / Done) — drives the board's "hide Done older than N days" declutter (AVM-154).
  statusCategoryChangedAt?: string;
  // Completion Model 1.0 (docs/completion-model-1.0-spec.md): the canonical
  // completion timestamp — witnessed (source history / in-app move) or the
  // customer's declared file date; null/absent = unknown, disclosed on every
  // windowed metric, never approximated.
  completedAt?: string | null;
  // Provenance of completedAt (AVM-167): 'witnessed' | 'declared' | 'asserted'
  // (set by hand via the audited correction) — visible so an asserted date
  // never masquerades as a recorded one.
  completedAtSource?: 'witnessed' | 'declared' | 'asserted' | null;
  timeLogs?: TimeLog[];
  /** The SOURCE tool's own reported time-spent (Jira/ADO/Asana). Undated lifetime total.
   *  Avium-native `timeLogs` win where they exist; this is what every connector org actually
   *  has, and reading only `timeLogs` is why their boards showed no logged hours at all. */
  timeSpentHours?: number | null;
  ticketResources?: TicketResource[];
}

// ── Scroll-first board payloads (AVM-148) ────────────────────────────────────

/** GET /tickets with ?limit — one page of a column/group plus the true numbers. */
export interface TicketPage {
  tickets: JiraTicket[];
  /** True match count under the filters — the badge number (includes stale-done). */
  total: number;
  /** How many the ?excludeDoneBefore cutoff removed from the pages. */
  staleHidden: number;
  /** Whether another page of visible (non-stale) rows exists. */
  hasMore: boolean;
}

/** GET /tickets/board-summary — one per-status aggregate row. */
export interface BoardSummaryStatus {
  statusId: string | null;
  count: number;
  storyPoints: number;
  /** `null` = Avium could not read this status's effort. NEVER render it as 0. */
  loggedHours: number | null;
  assigned: number;
  staleDone: number;
}

/**
 * DB-side aggregates for every number the Workload used to derive from
 * downloading the full ticket set. perSource + demo + outOfSync describe the
 * PRE-source-filter scope (they feed the source menu and the sync banner).
 */
export interface BoardSummary {
  total: number;
  outOfSync: number;
  demo: { present: boolean; count: number };
  perStatus: BoardSummaryStatus[];
  perSource: Record<'jira' | 'azure' | 'asana' | 'excel' | 'avium' | 'unknown', number>;
}

export interface TicketResource {
  id: string;
  ticketId: string;
  resourceId: string;
  role?: string;
  ticket?: JiraTicket;
  resource?: Resource;
}

export interface TimeLog {
  id: string;
  resourceId: string;
  ticketId?: string;
  resource?: Resource;
  ticket?: JiraTicket;
  hours: number;
  logDate: string;
  notes?: string;
  loggedVia: 'manual' | 'jira' | 'timer';
}

export interface CommentAuthor {
  id: string;
  name: string;
  avatarUrl?: string | null;
}

export interface Comment {
  id: string;
  ticketId: string;
  // null when the author's account was deleted — the comment is anonymized
  // (rendered "Removed user") rather than removed. See Comment model SetNull.
  authorId: string | null;
  author?: CommentAuthor | null;
  body: string;
  mentions?: string[]; // userIds @-mentioned in this comment
  editedAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface TourStep {
  id: string;
  orgId: string;
  order: number;
  target?: string | null;
  placement?: 'bottom' | 'top' | 'right' | 'left' | null;
  route?: string | null;
  title: string;
  body: string;
  isActive: boolean;
}

export interface TimerSession {
  id: string;
  orgId: string;
  ticketId: string;
  resourceId: string;
  startedAt: string;
  stoppedAt?: string | null;
  autoStopped: boolean;
  warningsAcknowledged: number;
  dismissedAt?: string | null;
  resource?: Resource;
}

export interface FieldMapping {
  id: string;
  jiraField: string;
  ermField: string;
  label: string;
  enabled: boolean;
}

export interface BrandingConfig {
  id: string;
  key: string;
  value: string;
  category: string;
  label: string;
}

export interface CustomField {
  id: string;
  entity: string;
  fieldName: string;
  fieldType: string;
  options?: string;
  required: boolean;
  order: number;
}

export interface JiraConfig {
  id: string;
  baseUrl: string;
  email: string;
  projectKeys: string;
  /** Optional ingestion-scope JQL ANDed into every project's fetch (null = pull all). */
  scopeJql?: string | null;
  isActive: boolean;
  lastSynced?: string;
}

// Per-source NEUTRAL freshness (Task B reshape) — mirrors server lib/sourceFreshness.ts
// SourceFreshness. One entry per CONNECTED source (Jira/Azure/Asana + Excel) with its
// last-import DATE. NOT a warning: freshness is calm context (the "Data freshness"
// strip); the warning is DataCoverage.anyIncomplete.
export interface SourceFreshness {
  source: 'jira' | 'azure' | 'asana' | 'excel';
  lastImport: string | null;
}

// Coverage/truncation — the ONE data-dropped warning (mirrors server DataCoverage).
// Connector cap (Task A, PROD/`/v1`-stable) + Excel row-limit truncation (Task B).
export interface DataCoverage {
  capReached: boolean;
  skippedCount: number;
  capReachedAt: string | null;
  excelTruncated: boolean;
  excelDroppedRows: number;
  excelImportAt: string | null;
  anyIncomplete: boolean;
}

// One requirement's effort provenance — where a number came from and how much of the
// data we could read (mirrors server lib/effortSource EffortCoverage['planned'|'actual']).
// Drives the Dashboard's State-C provenance strip. `configured: false` = render dark, never 0.
export interface EffortCoverageSlice {
  configured: boolean;
  origin: 'avium' | 'source' | 'source_rollup' | 'none';
  ticketsWithData: number;
  ticketsTotal: number;
}

export interface DashboardOverview {
  totalResources: number;
  totalTickets: number;
  totalHours: number;
  // `null` (sent as such) when Avium could not resolve logged effort — the client MUST render
  // it dark, not as 0 (RULE 24). Coverage travels with each number so the provenance strip can
  // say WHERE it came from and WHY it's partial. Optional so an older cached payload just omits it.
  totalHoursCoverage?: EffortCoverageSlice;
  plannedTotal?: number | null;
  plannedCoverage?: EffortCoverageSlice;
  phases: Array<{ id: string; name: string; color: string; ticketCount: number }>;
  // Coverage/truncation warning (connector cap + Excel truncation). Optional so an
  // older cached payload just omits the header disclosure.
  dataCoverage?: DataCoverage;
  // Per-source NEUTRAL freshness (Task B reshape) — the connected set with last-import
  // dates; drives the calm "Data freshness" strip. Optional (older cached payloads).
  sourceFreshness?: SourceFreshness[];
}

export interface FiltersState {
  projectIds?: string[];
  sprintIds?: string[];
  fixVersionIds?: string[];
  departmentIds?: string[];
  teamIds?: string[];
  resourceIds?: string[];
  // Board Issue Type filter (Bug / Story / Task / Sub-task / …) — the last of the founding
  // mockup's five filters. Multi-value; the server scopes tickets by `issueType IN (…)`.
  issueType?: string[];
  // Parent / "family tree" scope (Knowledge Graph Focus drawer). When set, the
  // Knowledge Graph switches to the non-time-bound parent-anchored mode — the
  // full subtree under these parent ids, across all sprints. Sprint/release stay
  // optional narrowers. Coordinated with the server `IntelligenceOptions.parentIds`
  // and the `/v1` work-items `parentId` filter (App↔API field lockstep).
  parentIds?: string[];
  // Lens-switcher PREVIEW override (the cadence bar). Forces the briefing's
  // delivery-analytics engine (sprint vs kanban) for this session WITHOUT saving
  // it — a non-persistent view preference, not a data filter. Sent as
  // `?deliveryMode=` and consumed by IntelligenceOptions.deliveryMode. Undefined =
  // Auto (resolve from the saved org/team setting).
  lensOverride?: 'sprint' | 'kanban';
  search?: string;
  fromDate?: string;
  toDate?: string;
}

// A parent / "family tree" candidate surfaced by the /intelligence/parents
// endpoint — any ticket that is a parent of ≥1 other ticket, labelled by its
// NATIVE source issue type (Jira Epic, ADO Epic/Feature, Asana Project).
export interface ParentCandidate {
  id: string;
  key: string;
  summary: string | null;
  issueType: string;
  childCount: number;
}

// ── Knowledge Graph (Intelligence "Map" tab) ──────────────────────────────────
// Mirrors server/src/lib/knowledgeGraphService.ts — keep the two in lockstep.
export type GraphNodeType = 'signal' | 'person' | 'ticket' | 'sprint' | 'epic';
export type GraphEdgeKind = 'assignment' | 'sprint' | 'parent' | 'dependency' | 'signal';

export interface GraphNode {
  id: string;
  type: GraphNodeType;
  label: string;
  sublabel?: string;
  risk?: boolean;
  external?: boolean;
  meta?: Record<string, unknown>;
}

export interface GraphEdge {
  id: string;
  source: string;
  target: string;
  kind: GraphEdgeKind;
  risk?: boolean;
  label?: string;
}

// Mirrors the server's InsightPriority (lib/riskScoring.ts) — Avium Priority
// Score + the grounded "How it's calculated" breakdown. Kept structurally in sync
// with InsightsView's InsightPriority so PriorityBadge accepts a rail row's score.
export interface GraphSignalPriorityScore {
  score: number; rank: number; actionable: boolean;
  factors: Array<{ key: string; label: string; weight: number; value: number; contribution: number; grounding: string }>;
}
// One ranked row of the Knowledge Graph "Signals by priority" rail — a scored
// Avium Signal that maps to a spine node, so clicking it isolates that risk path.
// Critical signals sort first, so they read distinctly instead of buried in a count.
export interface GraphSignalPriority {
  nodeId: string;
  cardId: string;
  key: string;
  icon: string;
  title: string;
  count: number;
  severity: 'critical' | 'warning' | 'info' | 'positive';
  priority: GraphSignalPriorityScore;
}

export interface KnowledgeGraph {
  nodes: GraphNode[];
  edges: GraphEdge[];
  meta: {
    generatedAt: string;
    unit: 'hours' | 'points';
    unitLabel: string;
    sprintId: string | null;
    sprintName: string | null;
    scope: string;
    window: { start: string; end: string } | null;
    // crossSprintCount = the subset of blocked-by-dependency whose blocker is
    // outside the sprint (reconciles with the cross-sprint Signal card's count);
    // severity = the signal's rated severity (drives the critical-red chip);
    // score = the reason's Avium Priority Score (0–100), omitted when unscored
    riskBreakdown: Array<{ key: string; icon: string; label: string; count: number; crossSprintCount?: number; severity?: 'critical' | 'warning' | 'info' | 'positive'; score?: number }>;
    // Risk-path signals ranked by Avium Priority Score (critical first) — drives
    // the "Signals by priority" rail and the per-node severity coloring.
    signalsByPriority: GraphSignalPriority[];
    // Risk signals that aren't on the spine (velocity, data quality, …) — shown as
    // a "+N more in The Brief" count so the rail stays honest about its scope.
    offPathSignalCount: number;
  };
}

// Avium Agents — a "lever": a one-action change Avium recommends next to a
// detected Signal (CLAUDE.md Critical #10 — "every risk ships a lever"). The
// server derives these from the live IntelligencePayload and persists the
// accept/dismiss decision. The Apply/execution stays the existing drill flow.
// rebalance/unblock/clear/assign = sprint-capacity levers; nudge/reflow = kanban
// flow levers (nudge an item aging past the SLE; review a column over its WIP
// limit). Mirrors server LeverType in lib/leverConfig.ts — keep in sync.
// fill/calibrate = the B2/A2 capacity levers (Capacity Holistic Redesign P6):
// fill = an under-allocated person with sprint runway left; calibrate = stated
// capacity drifted from delivered history (proposes, never overrides).
// staged_reassign/staged_absence (2026-07-20, the user-staged-lane resolution)
// = the two moves the Capacity Scenario "Make it real" sheet stages — never
// DERIVED like the eight above; born applied (`origin: 'user_staged'` below)
// at the moment the underlying write lands.
// push_worklog (Work Center slice W2b) joins the same user-staged lane, but
// born 'accepted' — the Jira write is deferred to an explicit Apply on My
// Week (see `payload.timeLogId`). carry_forward (Work Center slice W3, the
// carry-action port) is the same shape — born 'accepted' from Capacity's
// Person grain drill drawer, its sprint-move (+ optional reassign) write
// deferred to Apply (see `payload.targetSprintId`/`reassignToResourceId`).
export type ActionItemType =
  | 'rebalance' | 'unblock' | 'clear' | 'assign' | 'nudge' | 'reflow' | 'fill' | 'calibrate'
  | 'staged_reassign' | 'staged_absence' | 'push_worklog' | 'carry_forward';
export type ActionItemStatus =
  | 'recommended' | 'accepted' | 'declined' | 'applied' | 'expired' | 'failed';
// 'signal': derived from a live Avium Signal, reconciled every sync run.
// 'user_staged': born applied (or, for push_worklog, 'accepted') from another
// surface's own staged move — the user's own history, never touched by
// reconciliation. Mirrors server ActionItemOrigin in lib/leverConfig.ts — keep
// in sync.
export type ActionItemOrigin = 'signal' | 'user_staged';

export interface ActionItem {
  id: string;
  type: ActionItemType;
  origin: ActionItemOrigin;
  doVerb: string;            // imperative shown to the user ("Rebalance")
  signalId: string;          // grounding — the Avium Signal this lever came from
  label: string;
  detail: string;
  targetType: 'ticket' | 'resource' | 'sprint' | 'status' | null;
  targetId: string | null;
  targetLabel: string | null;
  dataSource: string | null; // jira_import | azure_import | … (Phase-3 routing)
  /** Typed mutation spec (e.g. `{ timeLogId, ticketKey, hours, week }` for a
   *  push_worklog row) — read directly by a surface that needs its own
   *  lever's specifics (My Week's staged-push card); most readers never
   *  touch this. */
  payload: Record<string, unknown> | null;
  metricUnit: string | null;
  /** Who resolved this lever (accepted/declined/applied a signal-derived one,
   *  or staged a user-staged one) — null while still 'recommended'. */
  resolvedByUserId: string | null;
  status: ActionItemStatus;
  createdAt: string;
  updatedAt: string;
}

// Headline counts of the demo workspace, computed server-side from the canonical
// dataset and returned by GET /demo-data/status. The demo-description copy is
// built from these so it can never drift from the seeded data. Mirror of the
// server `DemoStats` in server/src/lib/demoDataset.ts.
export interface DemoStats {
  peopleCount: number;
  departmentCount: number;
  squadronCount: number;
  sprintSquadronCount: number;
  kanbanSquadronCount: number;
  closedSprintCount: number;
  activeSprintCount: number;
  futureSprintCount: number;
  ticketCount: number;
  ticketCountApprox: number;
  backlogCount: number;
}

// Shape of GET /demo-data/status (the bits the client consumes).
export interface DemoStatusResponse {
  present: boolean;
  hasRealData: boolean;
  seeding: boolean;
  error: string | null;
  step: string | null;
  stats: DemoStats;
}

// Avium API key (machine credential for /v1). The list/create/rotate responses
// never include the secret hash; the plaintext `secret` is returned separately
// by create + rotate and shown to the admin exactly once.
export interface ApiKey {
  id: string;
  name: string;
  prefix: string;
  last4: string;
  scopes: string[];
  createdByUserId: string | null;
  lastUsedAt: string | null;
  expiresAt: string;
  revokedAt: string | null;
  rateLimitPerMin: number | null;       // overall (read) request ceiling/min; null = plan default
  writeRateLimitPerMin: number | null;  // stricter write ceiling/min; null = the write default
  createdAt: string;
}

// ── EFFORT ROLLUP SURFACE (AVM-245) ─────────────────────────────────────────
// The /rollup page's payload — a nested hierarchy with three metrics rolled up
// each column. Mirrors server/src/lib/rollupTreeService.ts (app + shape move in
// lockstep, RULE 15). Every rolled number carries its `descendantIds` so the
// client can decompose any column to its contributing tickets (RULE 26), and
// every node carries `sourceUrl` so each row walks home to its source tool.
export type RollupMetricKey = 'logged' | 'committed' | 'estimated';

export interface RollupNodeMetric {
  own: number | null;
  descendants: number | null;
  total: number | null;
  contributingCount: number;
  descendantIds: string[];
  ownCoveredByChildren: boolean;
}

export interface RollupTreeNode {
  id: string;
  key: string;
  summary: string;
  issueType: string | null;
  assigneeName: string | null;
  sourceUrl: string | null;
  sourceLabel: string;
  hasChildren: boolean;
  metrics: Partial<Record<RollupMetricKey, RollupNodeMetric>>;
  /** Provenance of the node's Committed value (unified resolver, 2026-07-20):
   *  'avium' = any contributing slice was typed in Avium; 'source' = every
   *  contributor was projected from the customer's tool; null = no value.
   *  Same vocabulary as My Week's `committedOrigin` (myWeekConfig renders
   *  both). Optional so a pre-unification payload degrades to "no note". */
  committedOrigin?: 'avium' | 'source' | null;
  children: RollupTreeNode[];
}

export interface RollupColumn {
  key: RollupMetricKey;
  label: string;
  definition: string;
  coverage: { withValue: number; total: number };
}

export interface RollupTree {
  unit: 'hours' | 'points';
  unitSymbol: string;
  columns: RollupColumn[];
  roots: RollupTreeNode[];
  scopeTotals: Partial<Record<RollupMetricKey, number | null>>;
  itemCount: number;
  policy: { definition: string; boardDivergence: string; faceValue: string };
}

// ── ROLLUP PICKER — the query-entered picker's candidate search (AVM-051) ──
// GET /api/rollup/epics — the arrival picker's search-and-check candidate list.
// Mirrors server/src/lib/rollupTreeService.ts's RollupRootCandidate /
// RollupRootSearchResult (app + shape move in lockstep, Law 8).
export interface RollupRootCandidate {
  id: string;
  key: string;
  summary: string;
  issueType: string | null;
  sourceLabel: string;
  /** Every ticket beneath this root (any depth), NOT counting the root itself. */
  descendantCount: number;
  /** Of those descendants, how many are not yet done. */
  openDescendantCount: number;
  hasChildren: boolean;
}

export interface RollupRootSearchResult {
  total: number;
  /** Echoes the clamp actually applied — an honest, disclosed pagination bound, never a silent
   *  cap (Law 1). */
  limit: number;
  offset: number;
  items: RollupRootCandidate[];
}

// ── THE JQL POWER LANE (AVM-051 Concept 4 / Work Center slice W5, Jira-first) ─────────────────
// GET /api/rollup/epics?jql=… — a sibling mode on the same endpoint: the practitioner's own JQL,
// run verbatim against their live Jira, resolved back to LOCAL synced epics only. Mirrors
// server/src/lib/rollupTreeService.ts's RollupRootJqlSearchResult.
export interface RollupRootJqlSearchResult extends RollupRootSearchResult {
  /** Jira issue keys the query matched that are NOT (yet) synced into Avium's own JiraTicket
   *  table — reported honestly, never silently dropped (Law 1); the customer's own signal to
   *  sync before an item can be picked here. */
  unmatchedKeys: string[];
}

// ── DEPARTMENTS & TEAMS (Capacity Holistic Redesign, state A) ──────────────
// GET /api/planning/department-summaries — the manager view's rollup of the
// SAME per-person matrix the Sprint Matrix reads (buildMatrixRows, same
// override + absence fold), grouped by department for the active-sprint
// window. `UtilizationBand` is imported from capacityFormat.ts — the client's
// one mirror of the server's band vocabulary (RULE 19: one home, never a
// second definition).
export interface DeptSummaryMember {
  resourceId: string;
  name: string;
  teamName: string | null;
  /** null = NOT SET (Resource.capacity has no null sentinel; 0 means unset). */
  capacity: number | null;
  committed: number;
  utilizationPct: number | null;
  band: UtilizationBand;
  availability: number | null;
  /** An explicit sprint-capacity override applied to this person — never an
   *  auto-deduced absence adjustment (that folds into `capacity` silently). */
  hasOverride: boolean;
  /** This person's cadence, via their team (AVM-273 → AVM-385). 'kanban' means
   *  their work runs on the flow board, so a 0 committed here is a CADENCE
   *  fact, not a planning gap — the row says so rather than reading blank. */
  deliveryMode: 'sprint' | 'kanban';
}

// `DeptSummaryDepartment` doubles as a TEAM group when `?groupBy=team` is
// requested (Capacity Holistic Redesign, state A — the "Group: Department ▾
// (or Team)" toggle, COMPLETION 1, approved 2026-07-18): `id`/`name`/`color`
// then read a Team instead of a Department (id 'no-team' for people with no
// team). Teams now carry the SAME Planning Baseline concept as departments
// (Team.capacity/pointsBaseline, AVM-330 D2 twin) — `baseline`/
// `baselineAttainmentPct` are null only when no manager has set a target for
// that group, exactly like an unset department.
export interface DeptSummaryDepartment {
  id: string;
  name: string;
  color: string;
  /** The GROUP's cadence (AVM-385). A team resolves to exactly one mode; a
   *  department can straddle both and reads 'mixed' rather than picking a
   *  winner. A wholly-'kanban' group's zero committed is expected — the row
   *  labels it instead of rendering as an under-committed sprint team. */
  deliveryMode: 'sprint' | 'kanban' | 'mixed';
  people: { set: number; total: number };
  /** Σ of SET members' effective capacity only; null when zero members are set. */
  capacity: number | null;
  /** Department.capacity (hours) / Department.pointsBaseline (points) for a
   *  department group, Team.capacity/pointsBaseline for a team group — the
   *  manager-typed target, shown BESIDE capacity, never substituted for it.
   *  Null when unset. */
  baseline: number | null;
  baselineAttainmentPct: number | null;
  /** Real work — always sums, even when nobody in the department has set capacity. */
  committed: number;
  utilizationPct: number | null;
  band: UtilizationBand;
  availability: number | null;
  members: DeptSummaryMember[];
}

// A6 — the forecast calibration (back-test) pair the On-time Confidence tile
// and the flow pulse's forecast stat both carry. `null` from the server
// whenever there isn't enough resolved history to grade — never a fabricated
// pair (Law 1). ONE home for this shape (server/src/lib/forecastCalibration.ts
// mirrors it) — never duplicated into a second consumer.
export interface ForecastCalibration {
  statedAvgPct: number;
  landedPct: number;
  samples: number;
}

// The On-time Confidence tile (Capacity Holistic Redesign, state A,
// COMPLETION 2, docs/design/capacity-redesign-hifi.html#state-a) — the SAME
// Monte-Carlo sprint forecast Avium Signals already runs
// (sprintForecastService.computeSprintForecast), never a second formula.
// `null` whenever the engine's own gates say no forecast — the tile then
// renders the dark "needs history" state (state-h), never a fabricated %.
export interface DeptSummaryConfidence {
  probabilityOnTime: number;
  targetDate: string;
  calibration: ForecastCalibration | null;
}

export interface DeptSummaryHeadline {
  committed: number;
  capacity: number;
  utilizationPct: number | null;
  band: UtilizationBand;
  availability: number | null;
  /** How many of the org's active people have a capacity SET — never blended
   *  into the numerator/denominator above. */
  coverage: { set: number; total: number };
}

export interface DeptSummaryWindow {
  sprintId: string;
  name: string;
  startDate: string | null;
  endDate: string | null;
}

export interface DepartmentSummariesResponse {
  unit: 'hours' | 'points';
  /** null = no active sprint — an honest empty shape, never a fabricated window. */
  window: DeptSummaryWindow | null;
  headline: DeptSummaryHeadline | null;
  /** null whenever there's an active sprint but the forecast engine's own
   *  gates say no forecast (insufficient history / no throughput / already
   *  complete) — the tile renders dark, never a fabricated percentage. */
  confidence: DeptSummaryConfidence | null;
  departments: DeptSummaryDepartment[];
}

// ── THE TRUST PANEL (Capacity Holistic Redesign, state H — dark & setup
// states, docs/design/capacity-redesign-hifi.html#state-h). GET
// /api/planning/capacity-trust — "how solid is this page?" Every count comes
// from a canonical layer already read elsewhere on Capacity (see the route's
// own comment); this is a shaping response, not a second formula.
export interface CapacityTrustResponse {
  unit: 'hours' | 'points';
  /** null = no active sprint — an honest empty shape, never a fabricated window. */
  window: DeptSummaryWindow | null;
  /** Active people with capacity SET, out of every active person — the same
   *  rule /department-summaries' headline.coverage uses. */
  capacity: { set: number; total: number };
  /** Active-sprint items carrying ANY resolved planned effort, out of every
   *  active-sprint item. */
  estimates: { withEstimate: number; total: number };
  /** Of the items WITH an estimate, how many resolved from the customer's own
   *  connected source rather than something typed in Avium — with the source's
   *  name when one connector dominates, null when mixed or absent. */
  sourceDerived: { count: number; sourceLabel: string | null };
  /** Any ResourceAbsence row exists anywhere in the org — a calendar fact,
   *  independent of the active-sprint window above. */
  absencesConfigured: boolean;
  /** Departments this active sprint's work references (via `plannedEffort`'s
   *  canonical `byDepartment` fold) that NO active resource maps to —
   *  state-h's Ops row ("0 people mapped … check your mapping"). Empty when
   *  every referenced department has at least one active person, or when
   *  there is no active sprint. `committed` is already in the org's unit. */
  departmentsUnmapped: Array<{ id: string; name: string; committed: number }>;
}

// ── PEOPLE VIEW (Capacity Holistic Redesign, state B) ──────────────────────
// GET /api/planning/sprint-matrix — the scrum-master view's people × sprints
// grid. This is the CANONICAL client shape for the matrix payload; the older
// ad hoc interfaces inside SprintMatrixView.tsx predate it and are noted as
// debt (RULE 19: one home), not touched here.
export interface SprintMatrixSprint {
  id: string;
  name: string;
  state: string;
  startDate: string | null;
  endDate: string | null;
}

export interface SprintMatrixCellItem {
  id: string;
  ticketId: string;
  ticketKey: string;
  ticketSummary: string;
  value: number;
  editable: boolean;
  origin?: string;
}

export interface SprintMatrixCell {
  committed: number;
  capacity: number;
  utilizationPct: number | null;
  band: UtilizationBand;
  editable: boolean;
  items: SprintMatrixCellItem[];
  /** Work slipping in from the active sprint (AVM-276) — its OWN number, never
   *  folded into `committed`. Only the next sprint's cell carries a value. */
  carriedIn?: number | null;
}

export interface SprintMatrixCrossTeamClaim {
  teamId: string;
  teamName: string;
  hoursPerDay: number;
}

export interface SprintMatrixRow {
  resourceId: string;
  resourceName: string;
  defaultCapacity: number;
  cells: Record<string, SprintMatrixCell>;
  deliveryMode?: 'sprint' | 'kanban';
  /** Resource.department.name — the identity sub-line (mirrors
   *  DeptSummaryMember.teamName); null when the person has no department. */
  departmentName: string | null;
  /** Resource.team.name — the identity sub-line's second half (Brian,
   *  2026-07-20: a person's department says which function, their team says
   *  which board their hours land on). This is the PRIMARY team on their own
   *  record — distinct from `crossTeam` below, which is the additional-claims
   *  wedge. Null when they are on no team; the client renders that in words
   *  (lib/identityLine.ts), never as a bare dash. */
  teamName: string | null;
  /** Capacity − committed − carriedIn for the PRIMARY (active, or fallback
   *  closed) sprint only — null when that sprint has no capacity to divide by. */
  availability: number | null;
  /** An explicit ResourceCapacityOverride for the primary sprint — never an
   *  auto-deduced absence adjustment (that folds into the cell's `capacity`
   *  silently). */
  hasOverride: boolean;
  /** Raw PTO hours deducted for the primary sprint; 0 if none (hours mode only). */
  absenceDeducted: number;
  /** The cross-team wedge (Phase 2 scope, AVM-255): set only when a
   *  PersonAllocation row puts this person on >1 team, or off their primary
   *  team — never fabricated when the table is empty. */
  crossTeam: { teams: SprintMatrixCrossTeamClaim[] } | null;
}

export interface SprintMatrixResponse {
  unit: 'hours' | 'points';
  sprints: SprintMatrixSprint[];
  rows: SprintMatrixRow[];
}

// ── PERSON-GRAIN SCENARIO (AVM-332 SLICE 3, Capacity Center) ────────────────
// POST /api/planning/person-scenario — types copied 1:1 from server/src/lib/
// personScenario.ts (never reinvented): a lever RESHAPES the SAME canonical
// matrix + Monte-Carlo forecast inputs the rest of Capacity already reads —
// baseline and scenario run through ONE formula, never two (Law 1).

/** "Move this ticket's effort to a different person." */
export interface MoveTicketLever {
  type: 'move_ticket';
  ticketId: string;
  toResourceId: string;
  /** The row the user moved FROM (2026-07-19 adversarial-review fix) — the
   *  reshape touches ONLY this person's slice of the ticket's attribution,
   *  never every attributed row on a multi-person ticket. Set at popover
   *  time (the row the popover opened on), never inferred. */
  fromResourceId: string;
}

/** "What if this person is out N days, in this sprint?" Hours-mode only this
 *  slice — server/src/lib/personScenario.ts's file header states why (story
 *  points have no daily rate to convert days-off into a deduction). */
export interface HypotheticalAbsenceLever {
  type: 'hypo_absence';
  resourceId: string;
  sprintId: string;
  days: number;
}

/** "Cut (or grow) the sprint's remaining scope by a %, for the CONFIDENCE
 *  forecast only" (AVM-348) — the What-If tab's own scope lever, folded into
 *  Scenario mode's popover (client/src/components/capacity/
 *  CapacityScenarioRibbon.tsx's AdjustSprintChip). Sprint-grain: no
 *  ticketId/resourceId — see server/src/lib/personScenario.ts's own doc
 *  comments. Forecast-only (Law 2: Avium never rewrites the source's own
 *  sprint scope) — CapacityMakeItRealSheet renders it as a non-stageable
 *  informational line, never a write. */
export interface CutScopeLever {
  type: 'cut_scope';
  scopeDeltaPct: number;
}

/** "Slip (or pull in) the sprint's deadline by N days, for the CONFIDENCE
 *  forecast only" (AVM-348) — the What-If tab's own deadline lever. Same
 *  reasoning as CutScopeLever above. */
export interface SlipDeadlineLever {
  type: 'slip_deadline';
  deadlineShiftDays: number;
}

/** "Add N hypothetical people, at the scope's own team-average capacity, for
 *  the CONFIDENCE forecast only" (AVM-348b) — the What-If tab's own
 *  "Add people" lever, folded into Scenario mode's popover (AdjustSprintChip).
 *  Sprint-grain: no ticketId/resourceId — see
 *  server/src/lib/personScenario.ts's AddPeopleLever doc comment. Forecast-only
 *  (Law 2: Avium never invents a real person) — CapacityMakeItRealSheet
 *  renders it as a non-stageable informational line, never a write. */
export interface AddPeopleLever {
  type: 'add_people';
  /** Clamped 1..20 server-side — the same stepper range the What-If tab's
   *  own "Add people" control offered. */
  count: number;
}

export type PersonLever = MoveTicketLever | HypotheticalAbsenceLever | CutScopeLever | SlipDeadlineLever | AddPeopleLever;

/** Why a lever could not be applied — never a silent drop (Law 1). */
export type PersonLeverRejectionReason = 'points_mode_unsupported' | 'no_daily_rate';

export interface PersonLeverRejection {
  leverIndex: number;
  lever: PersonLever;
  reason: PersonLeverRejectionReason;
  message: string;
}

/** A scenario-side matrix cell — the SAME shape SprintMatrixCell carries
 *  (committed/capacity/utilizationPct/band/editable/items/carriedIn); aliased
 *  rather than redeclared because the fold producing it
 *  (buildMatrixRowsFromInputs) is the identical pure core /sprint-matrix
 *  itself calls. */
export type PersonScenarioMatrixCell = SprintMatrixCell;

/** A scenario-side person row. Deliberately NARROWER than SprintMatrixRow —
 *  the engine's pure matrix core never carries departmentName / crossTeam /
 *  hasOverride / absenceDeducted / deliveryMode / availability; those are
 *  enrichments the /sprint-matrix ROUTE adds on top, and this endpoint
 *  doesn't. The client overlays these cell numbers onto the already-loaded
 *  /sprint-matrix rows by (resourceId, sprintId) — it never needs a second
 *  copy of that enrichment. */
export interface PersonScenarioMatrixRow {
  resourceId: string;
  resourceName: string;
  defaultCapacity: number;
  cells: Record<string, PersonScenarioMatrixCell>;
}

export interface PersonScenarioConfidence {
  probabilityOnTime: number;
  sprintEndDate: string | null;
}

export interface PersonScenarioSide {
  rows: PersonScenarioMatrixRow[];
  /** null = the engine cannot forecast this org's active sprint right now
   *  (no active sprint, insufficient history, …) — NEVER a fabricated 0
   *  (Law 1). The grid still renders; the ribbon says why not. */
  confidence: PersonScenarioConfidence | null;
}

export interface PersonScenarioResponse {
  unit: 'hours' | 'points';
  sprintWindow: Array<{ id: string; name: string; state: string; startDate: string | null; endDate: string | null }>;
  baseline: PersonScenarioSide;
  scenario: PersonScenarioSide;
  levers: { requested: PersonLever[]; applied: PersonLever[]; rejected: PersonLeverRejection[] };
}

// ── KANBAN FLOW CAPACITY GRID (Capacity Holistic Redesign, state G) ─────────
// GET /api/planning/flow-grid — the person × pace/WIP/aging/typical-finish
// grid for ONE flow team. Item-count based (no hours-or-points math — a
// deliberate difference from the sprint matrix above: a kanban team's
// "capacity" is its demonstrated pace, not a committed/capacity hours split).
export interface FlowGridAgingFact {
  count: number;                              // items past the team's own Delivery Time Target
  oldest: { key: string; days: number } | null; // the oldest BREACHING item — null iff count is 0
}

// Pulse band third stat (Capacity Holistic Redesign, state G, COMPLETION 2) —
// "Next N items: X% by <date>". `calibration` shares the SAME shape the
// On-time Confidence tile carries (ForecastCalibration, above) — populated by
// the /flow-grid route from forecastCalibration.ts's
// computeFlowForecastCalibration; `null` until enough daily captures have
// resolved (see that function's doc comment, server/src/lib/forecastCalibration.ts).
export interface FlowGridForecast {
  itemCount: number;
  probabilityPct: number;
  byDate: string; // ISO date
  calibration: ForecastCalibration | null;
}

export interface FlowGridPulse {
  throughputPerWeek: number | null; // null below the team's own 6-week history floor
  weeklySpark: number[];            // 8 entries oldest→newest, always real (never gated)
  wip: number;
  wipWeeksOfWork: number | null;    // wip ÷ throughputPerWeek; null whenever throughput is null/0
  wipBand: 'high' | null;
  aging: FlowGridAgingFact;
  // The team's OWN median cycle time — the pinned "everyone" row's Typical-finish
  // cell (additive: not a client-side re-derivation from the visible person
  // rows). Shares throughputPerWeek's history floor.
  typicalFinishDays: number | null;
  // null below the SAME history floor as the rest of this band, or when the
  // team's trailing window has no throughput to bootstrap from.
  forecast: FlowGridForecast | null;
}

export interface FlowGridPerson {
  resourceId: string;
  name: string;
  departmentName: string | null;
  pace: number | null;              // finished/wk, trailing 8 weeks; null below this PERSON's own 6-week floor
  spark: number[] | null;           // 8 entries; null exactly when pace is null
  wip: number;                      // never gated — a live fact
  aging: FlowGridAgingFact;         // never gated — a live fact
  typicalFinishDays: number | null; // median start→done; shares pace's history floor
  // This person's own weeks of finished-item history — always reported (even
  // below the floor), so the dark cell can say "N wks — needs 6" with a real N.
  historyWeeks: number;
}

export interface FlowGridTeamSummary {
  id: string;
  name: string;
  historyWeeks: number;
}

export interface FlowGridResponse {
  // Every kanban-resolved team in the org (name-sorted) — the state-G team
  // switcher's source list. Additive to the design's literal per-team shape:
  // the switcher needs something to switch across.
  availableTeams: FlowGridTeamSummary[];
  // null only when the org has NO flow team in scope at all (an honest empty
  // state, never a fabricated grid).
  team: FlowGridTeamSummary | null;
  pulse: FlowGridPulse | null;
  /** AVM-332 SLICE 5: the pulse band's own disclosure — it is ALWAYS
   *  whole-team math (server/src/lib/flowEngine.ts's FlowGridAnalytics),
   *  never narrowed by a people lens even when the rows below it are. `null`
   *  only alongside `team: null` (no flow team in scope at all). */
  pulseScope: 'team' | null;
  people: FlowGridPerson[];
}

// ── WORK (Capacity Holistic Redesign, state C — Work + What-If; this shape is
// the FIRST SLICE only: the demand view, without the What-If overlay).
// GET /api/planning/work-demand — rows are epics, columns are the active
// sprint + next 2 future sprints, reusing computeEpicDemand (lib/epicDemand)
// for every demand number so this view can never disagree with the Work vs
// Capacity tab (/epic-forecast) about what a body of work costs.
export interface WorkViewWindowSprint {
  sprintId: string;
  name: string;
  state: string;
  startDate: string | null;
  endDate: string | null;
}

export interface WorkViewCellItem {
  id: string;
  key: string;
  summary: string;
  /** This item's OWN effort — `null` when it carries no estimate. */
  value: number | null;
}

export interface WorkViewCell {
  /** Σ own effort of this epic's items committed to this sprint. `null` =
   *  no items land here, or none carry an estimate — never a fabricated 0. */
  demand: number | null;
  /** The items behind `demand` — walkable (Law 1), and what the What-If
   *  overlay's "move a ticket" lever picks from (state C, COMPLETION 4). */
  items?: WorkViewCellItem[];
}

export interface WorkViewEpic {
  /** Stable identity for React keys — the epic key, or the "no epic" sentinel. */
  id: string;
  /** null for the "no epic" bucket — never the internal sentinel string. */
  key: string | null;
  summary: string;
  /** null when the epic's items span more than one team, or none resolve to
   *  a team — Avium will not assert a single owner for split work. */
  teamName: string | null;
  /** Not-yet-Done items under this epic, across the whole window. */
  openItems: number;
  cells: Record<string, WorkViewCell>;
}

export interface WorkViewFooterCell {
  /** Σ demand across every epic for this sprint. `null` when nothing in the
   *  sprint carries an estimate — never a fabricated 0. */
  demand: number | null;
  /** Σ per-person effective capacity for this sprint (the same buildMatrixRows
   *  override + absence fold every capacity route shares). */
  supply: number;
  utilizationPct: number | null;
  /** null alongside a null demand — there is no honest verdict to color. */
  band: UtilizationBand | null;
}

export interface WorkViewResponse {
  unit: 'hours' | 'points';
  /** Empty = no active sprint — an honest empty shape, never a fabricated window. */
  window: WorkViewWindowSprint[];
  epics: WorkViewEpic[];
  footer: Record<string, WorkViewFooterCell>;
}

// ── WHAT-IF SCENARIO OVERLAY (state C, COMPLETION 4:
// docs/design/capacity-redesign-hifi.html#state-c). Two lever shapes this
// slice, matching server/src/lib/workViewScenario.ts exactly (one type, one
// place — server/src/lib/<feature>.ts's mirror). POST
// /planning/work-demand/scenario returns the SAME WorkViewResponse shape,
// computed with these levers applied — B1 (D7-committed): an overlay on the
// ONE calculator, never a parallel math path. Nothing persists; the client
// diffs this response against the baseline it already has.
export interface MoveSprintLever {
  type: 'move_sprint';
  ticketId: string;
  toSprintId: string;
}

export interface AddSupplyLever {
  type: 'add_supply';
  resourceId: string;
  sprintId: string;
  hours: number;
}

export type ScenarioLever = MoveSprintLever | AddSupplyLever;

// ── THE DRILL DRAWER (Capacity Holistic Redesign, state D — every number
// walks, docs/design/capacity-redesign-hifi.html#state-d). GET
// /api/planning/capacity-drill?level=department|person&id=...&sprintId=...
// walks DOWN one level at a time: a department decomposes to its people (Σ
// reconciles to what you clicked); a person decomposes to their tickets (Σ
// reconciles to what you clicked). Reuses the SAME buildMatrixRows
// calculation the calling view already rendered, so the drawer's sums can
// never disagree with the number that opened it.
export interface CapacityDrillPerson {
  resourceId: string;
  name: string;
  committed: number;
  /** null = NOT SET (Resource.capacity has no null sentinel; 0 means unset). */
  capacity: number | null;
  utilizationPct: number | null;
  band: UtilizationBand;
  /** Cadence via their team (AVM-385) — a flow person's 0 is expected. */
  deliveryMode: 'sprint' | 'kanban';
}

// Also the TEAM-level drill response (`level: 'team'`, COMPLETION 1 of the
// group-by toggle, approved 2026-07-18) — mirrors the department shape
// exactly (buildMatrixRows doesn't care which grouping produced the
// resource list), so this one interface serves both.
export interface CapacityDrillDepartmentResponse {
  level: 'department' | 'team';
  title: string;
  committed: number;
  /** null when zero people in the department/team have a capacity set. */
  capacity: number | null;
  /** The drilled group's cadence (AVM-385) — 'kanban' explains a list of
   *  people who all read 0; 'mixed' when a department straddles both. */
  deliveryMode: 'sprint' | 'kanban' | 'mixed';
  /** Every person, never a truncated preview (Law 1) — the client's own
   *  summary line states its exact bound ("+ N more, Xh"). */
  people: CapacityDrillPerson[];
}

export interface CapacityDrillSlice {
  /** A department name (a decomposed DepartmentAllocation row) — never a raw id. */
  deptOrResourceLabel: string;
  hours: number;
}

export interface CapacityDrillTicket {
  id: string;
  key: string;
  summary: string;
  planned: number;
  /** WHERE the number came from (Law 1/2 — the mapper's own vocabulary, never
   *  new jargon): 'allocations' = decomposed DepartmentAllocation rows (the
   *  per-department breakdown rides in `slices`); 'avium' = a single
   *  Avium-native ticket-level estimate; 'source' = the connector's own
   *  estimate (Original Estimate / story points), read-only. */
  origin: 'allocations' | 'avium' | 'source';
  slices: CapacityDrillSlice[] | null;
  /** Backward-looking "this ticket already carried in from a prior sprint" —
   *  read from Jira's own sprint-change history (AVM-268): `true` = measured,
   *  a qualifying sprint-change row exists; `false` = measured, Jira-sourced
   *  with history ingested and no qualifying row; `null` = unmeasurable —
   *  this connector's sprint history isn't ingested, so "not carried" would
   *  be a guess, never a claim (Law 1). */
  carriedIn: boolean | null;
  /** Opens the ticket in Jira / Azure DevOps / Asana; `null` for an imported
   *  file — nowhere to walk to (RULE 26 clause B). */
  sourceUrl: string | null;
  sourceLabel: string;
}

export interface CapacityDrillPersonResponse {
  level: 'person';
  title: string;
  committed: number;
  capacity: number | null;
  utilizationPct: number | null;
  band: UtilizationBand;
  /** This person's cadence (AVM-385) — 'kanban' explains an empty ticket list. */
  deliveryMode: 'sprint' | 'kanban';
  /** Every ticket, never a truncated preview (Law 1) — Σ planned reconciles
   *  exactly to `committed`. */
  tickets: CapacityDrillTicket[];
}

export type CapacityDrillResponse = CapacityDrillDepartmentResponse | CapacityDrillPersonResponse;

// ── THE CARRYOVER ENGINE'S RESPONSE (GET /api/carryover/sprint —
// server/src/lib/carryover.ts + routes/carryover.ts) — the active sprint's
// unfinished work as a claim on the next sprint, in the org's unit.
// Consolidated here (Work Center consolidation slice W4, docs/design/
// capacity-capability-ledger.md §E4 — the W3 debt CapacityDrillDrawer.tsx's
// own comment flagged): CapacityDrillDrawer.tsx used to declare its own
// narrower local copy of this exact server response. SprintMatrixView.tsx
// keeps its OWN pre-existing local copy untouched (out of scope for this
// slice, not a second canonical source going forward) — a small,
// acknowledged duplication rather than an unrequested refactor of a file
// this slice must not otherwise touch.
export type CarryoverRemainingOrigin =
  | 'avium' | 'source_remaining' | 'derived' | 'full_estimate' | 'points' | 'none';

/** One carried ticket's claim on the next sprint. */
export interface CarryoverSprintClaim {
  ticketId: string;
  key: string;
  summary: string;
  /** `null` = unattributable to a person (org total only, never dropped). */
  resourceId: string | null;
  resourceName: string | null;
  /** REMAINING effort, or `null` when the org has no estimate for it (Law 1 — never a fabricated 0). */
  remaining: number | null;
  origin: CarryoverRemainingOrigin;
  /** The parent epic this carried ticket belongs to. `null` = unparented work. */
  epicKey: string | null;
}

/** The Move-picker's headroom column — present only when the caller opts in
 *  via `?candidates=1`; every active person's next-sprint headroom, from the
 *  SAME calculator the sprint matrix itself reads (never re-derived). */
export interface CarryoverCandidate {
  resourceId: string;
  name: string;
  headroom: number | null;
}

export interface CarryoverSprintResponse {
  cadence: 'sprint' | 'flow';
  reason?: 'no_sprints' | 'no_active_sprint' | 'no_next_sprint';
  unit: 'hours' | 'points';
  fromSprint: { id: string; name: string } | null;
  toSprint: { id: string; name: string } | null;
  /** The COUNT form — how many tickets carry. Works with zero estimate/capacity data. */
  count: number;
  /** Σ remaining across claims, or `null` when nothing carries an estimate. */
  totalRemaining: number | null;
  unestimatedCount: number;
  /** Every carried ticket — the full set, never truncated. */
  claims: CarryoverSprintClaim[];
  byResource: Record<string, { name: string | null; count: number; remaining: number | null }>;
  unattributed: { count: number; remaining: number | null };
  /** Opt-in Move-picker headroom column (`?candidates=1`). */
  candidates?: CarryoverCandidate[];
}

// ── THE UNIVERSAL CAPACITY EDITOR (Capacity Holistic Redesign, state E — the
// paintable day-strip, docs/design/capacity-redesign-hifi.html#state-e). GET
// /api/planning/capacity-editor?resourceId=...&sprintId=... is the ONE
// payload the ONE editor component reads, from every entry point in the
// product (People, the Resources page, the drill drawer's footer button).
export interface CapacityEditorPerson {
  resourceId: string;
  name: string;
  departmentName: string | null;
  teamName: string | null;
}

export interface CapacityEditorSprint {
  id: string;
  name: string;
  startDate: string | null;
  endDate: string | null;
  /** The connector that owns this sprint's dates — 'avium' for one created
   *  in-app. Sprint.source is a first-class column, always populated. */
  source: 'jira' | 'azure' | 'asana' | 'excel' | 'avium';
}

export interface CapacityEditorWorkingDay {
  /** 'YYYY-MM-DD' */
  date: string;
  /** Hours painted OUT that day (0 = Working). Always in hours, regardless of
   *  the org's metric unit — absences are calendar facts (ResourceAbsence),
   *  and only hours-mode `effective` subtracts them (RULE 26). `null` is a
   *  points-org "day out" marker with NO clock-hours semantics — never a
   *  fabricated hours amount (2026-07-18 follow-through: killed the old
   *  POINTS_MODE_ABSENCE_FLAG_HOURS=1 placeholder, Law 1). */
  absenceHours: number | null;
  /** The day's classification, computed server-side so the client never
   *  re-derives the rate comparison: 'full' | 'half' | null (working). A
   *  `null`-hours row always reads 'full' — points mode has only presence,
   *  no fraction of a day to compare against a rate that doesn't exist. */
  out: 'full' | 'half' | null;
}

/** A pager neighbor — just enough to label and target the ‹ › chevrons. */
export interface CapacityEditorPagerSprint {
  id: string;
  name: string;
}

export interface CapacityEditorHistoryRow {
  before: number | null;
  after: number;
  unit: 'hours' | 'points';
  changedAt: string;
  changedBy: string | null;
  /** true = a BASE capacity change (audited with no sprint) shown alongside
   *  this sprint's override edits — it moves the sprint's effective number
   *  just the same. */
  base?: boolean;
}

export interface CapacityEditorResponse {
  unit: 'hours' | 'points';
  person: CapacityEditorPerson;
  sprint: CapacityEditorSprint;
  /** Chronological neighbors for the sprint pager (‹ ›), computed server-side
   *  over the org's dated sprints with no window cap — any future sprint is
   *  reachable, and closed ones page back for reference. Null on either side
   *  means the timeline genuinely ends there (or the current sprint is
   *  undated and has no place on the timeline at all). */
  pager: { prev: CapacityEditorPagerSprint | null; next: CapacityEditorPagerSprint | null };
  /** Resource.capacity / pointsCapacity by unit — null when unset (never a
   *  fabricated 0). */
  base: number | null;
  /** base ÷ working days — hours mode only; points have no daily rate
   *  (RULE 26) and an undated sprint has no window to divide by. */
  ratePerDay: number | null;
  /** Empty when the sprint carries no dates from its source — the client
   *  renders the honest "no dates" state instead of a strip. */
  workingDays: CapacityEditorWorkingDay[];
  /** This unit's ResourceCapacityOverride value for this sprint, if any. */
  override: number | null;
  /** The SAME number every other capacity surface shows for this person +
   *  sprint (identical fold to every other capacity route). */
  effective: number | null;
  /** Last 10 audit rows, most recent first. */
  history: CapacityEditorHistoryRow[];
  /** Points mode only — base pro-rated for the painted days-out fraction;
   *  null when nothing is painted out (nothing to suggest). Computed
   *  server-side — the client never invents this number. */
  suggestedProRatedPoints: number | null;
  /** THE STANDARD DAY (AVM-386) — how many hours this person works in a day,
   *  where that number came from, and what it implies for this sprint. */
  standardDay: CapacityEditorStandardDay;
}

export interface CapacityEditorStandardDay {
  /** The person's OWN rate. Null = they inherit the org's. */
  hoursPerDay: number | null;
  /** The org's standard working day. Null = nobody has told us. */
  orgHoursPerDay: number | null;
  /** What actually applies to this person: their own, else the org's, else null. */
  effectiveHoursPerDay: number | null;
  /** Provenance, so the surface can SAY where the number came from (Law 1).
   *  'unset' = we genuinely do not know — never render a plausible 8. */
  source: 'person' | 'organization' | 'unset';
  /** Working days in THIS sprint, by the org's own working week. 0 when undated. */
  workingDayCount: number;
  /** rate × working days — the derived DEFAULT for this sprint (decision 7:
   *  a typed per-sprint number still overrides it). Null in points mode, when
   *  the sprint is undated, or when no rate is known. */
  derivedBase: number | null;
}

// ── MY WEEK (Capacity Holistic Redesign, state F) ───────────────────────────
// GET /api/my-week — the per-person source-adaptive week surface. The whole
// point of this shape is `hasAviumWork`: a pure-connector person's board is
// entirely `typeable: false` rows and the client renders the read-only
// mirror; a person with ANY typeable row gets the timecard grid. Per-person,
// never a per-org mode switch.
export interface MyWeekDayAbsence {
  hours: number | null;
  out: 'full' | 'half' | null;
}

export interface MyWeekDay {
  date: string; // YYYY-MM-DD
  absence: MyWeekDayAbsence | null;
}

export interface MyWeekResource {
  id: string;
  name: string;
}

export interface MyWeekSummary {
  /** Hours logged via the Avium timer or manual entry this week. */
  loggedHere: number;
  /** Hours read from the connector's own worklogs this week. */
  syncedFromSource: number;
  /** The SAME sprint-capacity number every other Capacity surface shows for
   *  this person — never a re-derived "week" capacity. null = no sprint
   *  window, or no capacity set for this person. */
  sprintCapacity: number | null;
  sprintLabel: string | null;
  /** Raw PTO hours deducted from the base to reach sprintCapacity; 0 when an
   *  explicit override applies (the override already IS the human's number —
   *  no further deduction is shown on top of it). */
  absenceDeducted: number;
  /** The person's TRUE base capacity — server-sent, never reconstructed on
   *  the client as `sprintCapacity + absenceDeducted` (2026-07-18 review,
   *  finding #1): that reconstruction reads wrong once PTO exceeds the base,
   *  since `sprintCapacity` clamps at 0 while `absenceDeducted` keeps the
   *  full, uncapped PTO amount. null = no base capacity set. */
  baseCapacity: number | null;
}

export type MyWeekLoggedVia = 'manual' | 'timer' | 'synced';

export interface MyWeekCell {
  hours: number | null;
  via: MyWeekLoggedVia | null;
}

export interface MyWeekRow {
  ticketId: string;
  key: string;
  summary: string;
  source: 'avium' | 'jira' | 'azure' | 'asana' | 'excel';
  /** true for Avium-created (erm_created) tickets AND Jira-sourced ones (Work
   *  Center slice W2b) — the rows the timecard grid opens a cell for. Logging
   *  on a Jira row stages a `push_worklog` lever (see `ActionItem.payload`);
   *  logging on an Avium row is the original, plain in-app entry. Azure
   *  DevOps/Asana/Excel-sourced rows stay read-only. */
  typeable: boolean;
  status: string;
  /** Calendar days since this ticket entered "In Progress"; null when it
   *  isn't in that stage right now. */
  inProgressDays: number | null;
  /** THIS person's own share of the ticket's planned effort — Σ
   *  `DepartmentAllocation.plannedHours` rows naming this resource
   *  specifically (the same table the Epics tree's "Committed" column sums,
   *  filtered to "me" rather than every assignee). `null` = no allocation
   *  names this resource yet ("not planned"), never a fabricated 0. */
  committedHours: number | null;
  /** The item's OWN estimate only — never a rollup: the source tool's
   *  original estimate, falling back to an Avium-native planned hours field.
   *  `null` = this item carries no estimate at all. */
  estimatedHours: number | null;
  cells: Record<string, MyWeekCell>;
}

export interface MyWeekResponse {
  resource: MyWeekResource | null;
  week: { start: string; days: MyWeekDay[] } | null;
  summary: MyWeekSummary | null;
  rows: MyWeekRow[];
  hasAviumWork: boolean;
}

// ── THE MOMENT OF ASSIGNMENT (Capacity Holistic Redesign, state F) ─────────
// GET /api/planning/assignment-check — one candidate, one ticket: what does
// their utilization look like now, and what would it look like if this
// ticket became theirs. `current`/`ifAssigned` are the SAME calculator
// (capacityMetrics.planningLens) every other Capacity surface reads.
export interface AssignmentCheckLens {
  pct: number | null;
  band: UtilizationBand;
}

export interface AssignmentCheckResult {
  name: string;
  sprintId: string | null;
  sprintLabel: string | null;
  unit: 'hours' | 'points';
  /** How much this ticket would add to the candidate's committed load — 0
   *  when the ticket is already theirs (never double-counted). */
  addedValue: number;
  current: AssignmentCheckLens | null;
  ifAssigned: AssignmentCheckLens | null;
  availability: number | null;
  /** Set only when current/ifAssigned are null — e.g. no sprint window to
   *  check against. Never a guessed verdict. */
  reason?: string;
}
